/** @format */

import { reactive } from "vue";
import { useHead, type ReactiveHead } from "@unhead/vue";

/**
 * Document `<head>` management — the modern replacement for the old
 * `update-head` event-bus channel.
 *
 * `App.vue` calls `useDocumentHead()` once in setup to bind the shared reactive
 * head state to the document; each view calls `setDocumentHead(item)` on mount
 * to publish its title / description / Open Graph + Twitter image. Centralizing
 * the meta-building here keeps every view's SEO tags consistent.
 */
interface HeadItem {
  title?: string;
  description?: string;
  seo_title?: string;
  seo_description?: string;
  large_url?: string;
  medium_url?: string;
  image_url?: string;
  image?: string;
  /**
   * Open Graph object type. Defaults to "website"; product detail pages pass
   * "product". (Previously every page hardcoded "product".)
   */
  type?: "website" | "product";
}

const SITE_NAME = "Outstanding Photos";

// Same web origin + media base main.ts provides to the app. Read from env here
// because setDocumentHead is module-scoped and can't use Vue `inject`.
const WEB_URL = (import.meta.env.VITE_VUE_APP_WEBURL || "").replace(/\/$/, "");
const MEDIA_PATH = import.meta.env.VITE_VUE_APP_MEDIAPATH || "";
// Default social preview when a view supplies no image of its own.
const SITE_BANNER = `${MEDIA_PATH}static/banner5.jpg`;

/** Build an absolute canonical URL from the web origin + current path. */
function canonicalUrl(): string {
  const path =
    typeof window !== "undefined" && window.location
      ? window.location.pathname
      : "/";
  return `${WEB_URL}${path}`;
}

interface HeadMetaTag {
  name?: string;
  property?: string;
  content: string;
  id: string;
}

interface HeadLinkTag {
  rel: string;
  href: string;
  id: string;
}

interface CanonicalHeadExtra {
  title?: string;
}

/** Build the canonical + Open Graph URL fragment shared by reactive views. */
export function canonicalHead(
  url: string,
  extra: CanonicalHeadExtra = {},
): ReactiveHead {
  return {
    ...extra,
    link: [{ rel: "canonical", href: url, id: "canonical" }],
    meta: [{ property: "og:url", content: url, id: "og:url" }],
  };
}

const headState = reactive<{
  title: string;
  meta: HeadMetaTag[];
  link: HeadLinkTag[];
}>({
  title: SITE_NAME,
  meta: [],
  link: [],
});

/**
 * Build the resolved head tags for a view (pure — no reactivity). Extracted so
 * the SEO logic (canonical/og:url, banner image fallback, og:type) is unit
 * testable without unhead or a DOM.
 */
export function buildHead(item: HeadItem = {}): {
  title: string;
  meta: HeadMetaTag[];
  link: HeadLinkTag[];
} {
  // Prefer the per-photo SEO fields; fall back to title/description. The image
  // must be a real URL (the photo object exposes *_url, never `image`) so the
  // Open Graph / Twitter cards actually render a preview.
  const title = item.seo_title || item.title || "";
  const description = item.seo_description || item.description || "";
  // Fall back to the site banner so social cards always have a preview image.
  const image =
    item.large_url ||
    item.medium_url ||
    item.image_url ||
    item.image ||
    SITE_BANNER;
  const fullTitle = `${title} | ${SITE_NAME}`;
  const url = canonicalUrl();
  // Detail (product) pages pass type: "product"; everything else is a website.
  const ogType = item.type === "product" ? "product" : "website";

  return {
    title: fullTitle,
    meta: [
      { name: "description", content: description, id: "description" },
      { name: "twitter:title", content: fullTitle, id: "twittertitle" },
      { name: "twitter:image", content: image, id: "twitterimage" },
      { name: "twitter:description", content: description, id: "twitterdesc" },
      { property: "og:description", content: description, id: "ogdescription" },
      { property: "og:title", content: fullTitle, id: "ogtitle" },
      { property: "og:image", content: image, id: "ogimage" },
      { property: "og:type", content: ogType, id: "ogtype" },
      { property: "og:url", content: url, id: "og:url" },
    ],
    link: [{ rel: "canonical", href: url, id: "canonical" }],
  };
}

/** Publish the head tags for a view. Safe to call with a partial item. */
export function setDocumentHead(item: HeadItem = {}): void {
  const built = buildHead(item);
  headState.title = built.title;
  headState.meta = built.meta;
  headState.link = built.link;
}

/** Bind the shared head state to the document. Call once, in App.vue setup. */
export function useDocumentHead(): void {
  useHead(headState);
}
